import { useEffect, useRef, useState } from 'react';

import { cn } from './cn';
import type { SelectOption } from './Select';

/**
 * ListboxSelect — a single-choice dropdown whose OPEN PANEL is ours, not the
 * operating system's.
 *
 * `Select` wraps a native `<select>`: the closed control styles fine, but the
 * open menu is drawn by the OS and ignores the design system entirely. This is
 * the promoted, token-based sibling of the CRM's `CrmSelect` (same anatomy:
 * button trigger, floating listbox panel, outside-click + Escape dismissal,
 * real `option` roles) for surfaces where the open list must match the page —
 * the storefront's address picker is the founding use.
 *
 * Deliberately smaller than CrmSelect: no search, no footer action, no multi —
 * promote those only when a second app needs them.
 */

export interface ListboxSelectProps {
  readonly options: readonly SelectOption[];
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly placeholder?: string;
  /** Reaches the trigger button, so an existing `<Label htmlFor>` keeps working. */
  readonly id?: string;
  readonly ariaLabel?: string;
  readonly disabled?: boolean;
  readonly hasError?: boolean;
  /** Layout only — merged onto the wrapper. */
  readonly className?: string;
  readonly testId?: string;
}

export function ListboxSelect({
  options,
  value,
  onChange,
  placeholder = 'Select…',
  id,
  ariaLabel,
  disabled = false,
  hasError = false,
  className,
  testId,
}: ListboxSelectProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent): void => {
      if (ref.current !== null && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const selected = options.find((o) => o.value === value);

  return (
    // ⚠️ THE OPEN PANEL IS RAISED BY LIFTING THIS WRAPPER, NOT THE PANEL.
    // The panel's own `z-30` only orders it INSIDE the nearest stacking context,
    // and the storefront's cards each create one (each carries a `transform` for
    // its entry animation). So a panel that overhangs its card was painted UNDER
    // the next card however high its own z-index went — the card it lives in is
    // what has to win. `z-40` only while open, so a closed picker never steals
    // stacking from anything meant to sit above it.
    <div ref={ref} className={cn('relative w-full', open && 'z-40', className)}>
      <button
        type="button"
        {...(id !== undefined ? { id } : {})}
        {...(ariaLabel !== undefined ? { 'aria-label': ariaLabel } : {})}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-invalid={hasError || undefined}
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'flex h-10 w-full items-center gap-2 rounded-md border bg-bg px-3 text-left text-sm text-foreground transition-colors',
          'hover:border-primary/60',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
          'disabled:cursor-not-allowed disabled:opacity-50',
          hasError ? 'border-danger focus-visible:ring-danger' : 'border-border',
        )}
        {...(testId !== undefined ? { 'data-testid': testId } : {})}
      >
        <span className={cn('flex-1 truncate', selected === undefined && 'text-foreground/50')}>
          {selected?.label ?? placeholder}
        </span>
        <svg
          aria-hidden="true"
          className={cn(
            'h-4 w-4 shrink-0 text-foreground/50 transition-transform',
            open && 'rotate-180',
          )}
          viewBox="0 0 16 16"
          fill="none"
        >
          <path
            d="M4 6l4 4 4-4"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {open && (
        <div
          role="listbox"
          {...(ariaLabel !== undefined ? { 'aria-label': ariaLabel } : {})}
          /*
           * HEIGHT: short enough to stay inside the card it opens over, and
           * SCROLLED — never grown — when there are more options than fit.
           * `min(38vh,248px)` is about six rows. The previous
           * `min(50vh,360px)` ran 63px past the bottom of the storefront's
           * address card on a 768px-tall viewport, and that overhang vanished
           * behind the cards further down the page (issue #14553). Every option
           * stays reachable: `overflow-y-auto` keeps the list scrollable, and
           * `overscroll-contain` stops that scroll leaking into the page once
           * the panel reaches its end.
           */
          className="absolute left-0 right-0 z-30 mt-1 max-h-[min(38vh,248px)] overflow-y-auto overscroll-contain rounded-md border border-border bg-surface p-1 shadow-lg"
        >
          {options.length === 0 ? (
            /* An empty open panel reads as broken; say the honest thing. */
            <p className="px-2.5 py-2 text-sm text-foreground/50" role="status">
              {placeholder}
            </p>
          ) : (
            options.map((o) => (
              <button
                key={o.value}
                type="button"
                role="option"
                aria-selected={o.value === value}
                disabled={o.disabled}
                className={cn(
                  'flex w-full items-center rounded-[7px] px-2.5 py-2 text-left text-sm text-foreground',
                  'hover:bg-primary/10 hover:text-primary',
                  'disabled:cursor-not-allowed disabled:opacity-50',
                  o.value === value && 'font-semibold',
                )}
                onClick={() => {
                  onChange(o.value);
                  setOpen(false);
                }}
              >
                <span className="truncate">{o.label}</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
